/**
 * The MOS 2.8.5 protocol specifications can be found at:
 * https://drive.google.com/file/d/1dfO6Vj0ImT8IUnt3LI_dDZJdbtV8vPom/view?usp=sharing
 */
import truncate from 'lodash/truncate';
import { getExtname } from '../platformAbstraction/pathComposition';
import { ItemType } from '../dto/itemTypes';
import { getProxyFileNameAndMimeType } from '../item/media';
import { mediaTypeFromFilename } from '../util/mediaContentType';
import { createUuid } from '../util/createUuid';
import { invariant } from '../util/invariant';
import type {
  LogMessageEvent,
  MimirMetadataTag,
  MosAckStatus,
  MosContext,
  MosItemReplaceData,
  MosObjCompatibleItem,
  MosObjOptions,
  MosTag,
} from './mos-interfaces';
import { formatMosDate, getChild, getObjType } from './mos-util';
import xmldom from '@xmldom/xmldom';
import { truncateFileName } from '../util/path';

export const MIMIR_MOS_EXTERNAL_METADATA_SCHEMA = 'https://mjoll.no/metadata/xmlschema';

// The allowed XML characters are defined here: https://www.w3.org/TR/xml/#charsets
// eslint-disable-next-line no-control-regex
const XML_NOT_ALLOWED_CHARS = /[^\u0009\u000a\u000d\u0020-\uD7FF\uE000-\uFFFD\u{10000}-\u{10FFFF}]/gu;
function serializeSafe(node: Node) {
  return new xmldom.XMLSerializer()
    .serializeToString(node)
    .replace(XML_NOT_ALLOWED_CHARS, '');
}

export class MosBuilder {

  private doc = new xmldom.DOMParser().parseFromString('<root/>', 'text/xml');
  private readonly mosRoot: Element;
  private readonly mosID: string;

  constructor(context: MosContext) {
    this.mosRoot = this.doc.createElement('mos');
    this.mosID = context.mosID;

    this.createElementAndAppend(this.mosRoot, 'mosID', this.mosID);
    if (context.ncsID) {
      this.createElementAndAppend(this.mosRoot, 'ncsID', context.ncsID);
      if (context.userID) {
        this.createElementAndAppend(this.mosRoot, 'userID', context.userID);
      }
      if (context.messageID !== undefined) {
        this.createElementAndAppend(this.mosRoot, 'messageID', context.messageID);
      }
    }

    return this;
  }

  ncsReqAppInfo() {
    this.createElementAndAppend(this.mosRoot, 'ncsReqAppInfo');
    return this;
  }

  ncsAck(status: MosAckStatus, message?: string) {
    const ncsAck = this.createElementAndAppend(this.mosRoot, 'ncsAck');
    this.createElementAndAppend(ncsAck, 'status', status);
    if (message) {
      this.createElementAndAppend(ncsAck, 'statusDescription', message);
    }
    return this;
  }

  mosAck(status: MosAckStatus, statusDescription: string) {
    const mosAck = this.createElementAndAppend(this.mosRoot, 'mosAck');
    this.createElementAndAppend(mosAck, 'status', status);
    this.createElementAndAppend(mosAck, 'statusDescription', statusDescription);
    return this;
  }

  roAck(roId: string, errorDescription?: string) {
    const mosAck = this.createElementAndAppend(this.mosRoot, 'roAck');
    this.createElementAndAppend(mosAck, 'roID', roId);
    this.createElementAndAppend(mosAck, 'roStatus', errorDescription ?? 'OK');
    return this;
  }

  heartbeat(date: Date) {
    const heartbeat = this.createElementAndAppend(this.mosRoot, 'heartbeat');
    this.createElementAndAppend(heartbeat, 'time', formatMosDate(date));
    return this;
  }

  roReq(roId: string) {
    const roReq = this.createElementAndAppend(this.mosRoot, 'roReq');
    this.createElementAndAppend(roReq, 'roID', roId);
    return this;
  }

  listMachInfo(date: Date) {
    const listMachInfo = this.createElementAndAppend(this.mosRoot, 'listMachInfo');
    this.createElementAndAppend(listMachInfo, 'manufacturer', 'Mjoll');
    this.createElementAndAppend(listMachInfo, 'model', 'Mimir');
    this.createElementAndAppend(listMachInfo, 'hwRev', '');
    this.createElementAndAppend(listMachInfo, 'swRev', '');
    this.createElementAndAppend(listMachInfo, 'DOM', '');
    this.createElementAndAppend(listMachInfo, 'SN', '');
    this.createElementAndAppend(listMachInfo, 'ID', 'Mimir');
    this.createElementAndAppend(listMachInfo, 'time', formatMosDate(date));
    this.createElementAndAppend(listMachInfo, 'mosRev', '2.8.5');
    const supportedProfiles = this.createElementAndAppend(listMachInfo, 'supportedProfiles');
    supportedProfiles.setAttribute('deviceType', 'MOS');
    // Let's update the supported profiles when we support all messages
    // for a given profile
    this.createElementAndAppend(supportedProfiles, 'mosProfile0', 'YES');
    this.createElementAndAppend(supportedProfiles, 'mosProfile1', 'NO');
    this.createElementAndAppend(supportedProfiles, 'mosProfile2', 'NO');
    this.createElementAndAppend(supportedProfiles, 'mosProfile3', 'NO');
    this.createElementAndAppend(supportedProfiles, 'mosProfile4', 'NO');
    this.createElementAndAppend(supportedProfiles, 'mosProfile5', 'NO');
    this.createElementAndAppend(supportedProfiles, 'mosProfile6', 'NO');
    this.createElementAndAppend(supportedProfiles, 'mosProfile7', 'NO');
    return this;
  }

  ncsItem(mimirItem: MosObjCompatibleItem, options: MosObjOptions) {
    const ncsItem = this.createElementAndAppend(this.mosRoot, 'ncsItem');
    const item = this.createElementAndAppend(ncsItem, 'item');
    this.generateNcsItemDetails(item, mimirItem, options);
    return this;
  }

  /**
   * This is not according to spec, but what is currently in production with Octopus.
   * A <ncsItem> containing a <mosObj>, it should be an <item> as is generated in ncsItem function.
   *
   * TODO - replace this function call with ncsItem function, and test & verify in Octopus that everything works OK.
   */
  ncsItemWithMosObj(mimirItem: MosObjCompatibleItem) {
    const ncsItem = this.createElementAndAppend(this.mosRoot, 'ncsItem');
    const item = this.createElementAndAppend(ncsItem, 'item');
    this.generateMosItemDetails(item, mimirItem, {});
    return this;
  }

  mosObj(mimirItem: MosObjCompatibleItem, options: MosObjOptions) {
    const mosObj = this.createElementAndAppend(this.mosRoot, 'mosObj');
    this.generateMosItemDetails(mosObj, mimirItem, options);
    return this;
  }

  mosItemReplace(mimirItem: MosObjCompatibleItem, options: MosObjOptions, mosItemReplaceData: MosItemReplaceData) {
    const mosItemReplace = this.createElementAndAppend(this.mosRoot, 'mosItemReplace');
    this.createElementAndAppend(mosItemReplace, 'storyID', mosItemReplaceData.storyId);
    const item = this.createElementAndAppend(mosItemReplace, 'item');
    this.createElementAndAppend(item, 'itemID', mosItemReplaceData.itemIdInStory);
    this.generateMosItemDetails(item, mimirItem, options);
    return this;
  }

  private objPaths(mosObj: Element, item: MosObjCompatibleItem, options: MosObjOptions = {}) {
    const objPaths = this.createElementAndAppend(mosObj, 'objPaths');
    let pathPrefix = '';
    if (options.imageObjectPathPrefix && item.itemType === ItemType.IMAGE) {
      pathPrefix = `${options.imageObjectPathPrefix}`;
      if (!pathPrefix.endsWith('/')) {
        pathPrefix = `${options.imageObjectPathPrefix}/`;
      }
    }
    /*
      Property item.originalFileName only contains a basename, not a full path.
      Therefore it doesn't matter which path separator we use here.
    */
    const origSep = '/';
    const objPathSuffix = options.objectPathSuffix || getExtname(item.originalFileName ?? '', origSep);
    const objFilePath = truncateFileName(`${pathPrefix}${item.id}${objPathSuffix}`, options.objPathRules?.maxCharacters ?? null, item.id, '/');
    const objMediaType = options.objectPathSuffix ? mediaTypeFromFilename(objFilePath) : item.mediaType;
    const objPath = this.createElementAndAppend(objPaths, 'objPath', objFilePath);
    objPath.setAttribute('techDescription', objMediaType ?? 'unknown');

    // INCEPTION crashes on objProxyPath with values that exceed 255 characters, in accordance with MOS spec 2.8.5
    if (options.includeObjProxyPath) {
      const objProxyThumbnail = this.createElementAndAppend(objPaths, 'objProxyPath', item.thumbnail);
      objProxyThumbnail.setAttribute('techDescription', 'image/jpeg');

      const { mimeType: mediaType } = getProxyFileNameAndMimeType(item.itemType);
      if (item.proxy && mediaType !== null) {
        const objProxyPath = this.createElementAndAppend(objPaths, 'objProxyPath', item.proxy);
        objProxyPath.setAttribute('techDescription', mediaType);
      }
    }
  }

  serialize() {
    return serializeSafe(this.mosRoot);
  }

  serializeChild(childTagName: MosTag) {
    const child = getChild(this.mosRoot, childTagName);
    invariant(child, 'invalid state: no child of type: ' + childTagName);
    return serializeSafe(child);
  }

  private createElementAndAppend(parentElement: Element, tag: MosTag | MimirMetadataTag, textValue?: string | number) {
    const childElement = this.doc.createElement(tag);
    if (textValue) {
      const textNode = this.doc.createTextNode(textValue.toString());
      childElement.appendChild(textNode);
    }
    parentElement.appendChild(childElement);
    return childElement;
  }

  // http://mosprotocol.com/wp-content/MOS-Protocol-Documents/MOS_Protocol_Version_2.8.5_Final.htm#ncsItem
  private generateNcsItemDetails(parentElement: Element, item: MosObjCompatibleItem, options: MosObjOptions)  {
    this.createElementAndAppend(parentElement, 'objID', item.id);
    this.createElementAndAppend(parentElement, 'mosAbstract', item.title);
    // 2.8.5 spec requires us to set 0 value for itemID. NCS is responsible for generating it, we don't care about it.
    this.createElementAndAppend(parentElement, 'itemID', '0');
    this.createElementAndAppend(parentElement, 'mosID', this.mosID);
    this.createElementAndAppend(parentElement, 'itemSlug', truncate(item.title, { length: 128 }));
    this.createElementAndAppend(parentElement, 'objAir', item.hasHighres ? 'READY' : 'NOT READY');
    if (options.objTBFromNcs !== undefined && options.objDurFromNcs !== undefined) {
      this.createElementAndAppend(parentElement, 'itemEdDur', options.objDurFromNcs);
    } else if (item.mediaFrameRate) {
      // Default to 50 fields per second if we detect a 25fps framerate
      const objTB = item.mediaFrameRate === 25 ? 50 : item.mediaFrameRate;
      this.createElementAndAppend(parentElement, 'itemEdDur', Math.floor(((item.mediaDuration ?? 0) / 1000) * objTB));
    }
    const objType = getObjType(item.itemType);
    if (objType) {
      this.createElementAndAppend(parentElement, 'objType', objType);
    }
    this.createElementAndAppend(parentElement, 'itemEdStart', '0');
    this.objPaths(parentElement, item, options);
    this.generateExternalMetadata(parentElement, item, options);
  }

  // http://mosprotocol.com/wp-content/MOS-Protocol-Documents/MOS_Protocol_Version_2.8.5_Final.htm#mosObj
  private generateMosItemDetails(parentElement: Element, item: MosObjCompatibleItem, options: MosObjOptions) {
    this.createElementAndAppend(parentElement, 'objID', item.id);
    this.createElementAndAppend(parentElement, 'mosAbstract', item.title);
    this.createElementAndAppend(parentElement, 'objSlug', truncate(item.title, { length: 128 }));
    if (item.created) {
      this.createElementAndAppend(parentElement, 'created', formatMosDate(item.created));
    }
    if (item.changed) {
      this.createElementAndAppend(parentElement, 'changed', formatMosDate(item.changed));
    }
    if (options.status) {
      this.createElementAndAppend(parentElement, 'status', options.status);
    }
    this.createElementAndAppend(parentElement, 'objAir', item.hasHighres ? 'READY' : 'NOT READY');
    const objType = getObjType(item.itemType);
    if (objType) {
      this.createElementAndAppend(parentElement, 'objType', objType);
    }
    if (options.objTBFromNcs !== undefined && options.objDurFromNcs !== undefined) {
      this.createElementAndAppend(parentElement, 'objTB', options.objTBFromNcs);
      this.createElementAndAppend(parentElement, 'objDur', options.objDurFromNcs);
    } else if (item.mediaFrameRate) {
      // Default to 50 fields per second if we detect a 25fps framerate
      const objTB = item.mediaFrameRate === 25 ? 50 : item.mediaFrameRate;
      this.createElementAndAppend(parentElement, 'objTB', objTB);
      this.createElementAndAppend(parentElement, 'objDur', Math.floor(((item.mediaDuration ?? 0) / 1000) * objTB));
    }
    if (item.description) {
      this.createElementAndAppend(parentElement, 'description', item.description);
    }
    this.objPaths(parentElement, item, options);
    this.generateExternalMetadata(parentElement, item, options);
  }

  private generateExternalMetadata(
    parentElement: Element,
    item: MosObjCompatibleItem,
    options: MosObjOptions
  ) {
    const externalMeta = this.createElementAndAppend(parentElement, 'mosExternalMetadata');
    this.createElementAndAppend(externalMeta, 'mosScope', 'OBJECT');
    this.createElementAndAppend(externalMeta, 'mosSchema', MIMIR_MOS_EXTERNAL_METADATA_SCHEMA);
    const payload = this.createElementAndAppend(externalMeta, 'mosPayload');
    if (options.includeTimestamp) {
      this.createElementAndAppend(payload, 'metaTime', new Date().toISOString());
    }
    const metadata = this.createElementAndAppend(payload, 'metadata');
    const formLabel = item.exportFriendlyMetadata.formId;
    if (formLabel) {
      metadata.setAttribute('type', formLabel);
    }
    Object.entries(item.exportFriendlyMetadata.formData).forEach(([fieldId, fieldValue]) => {
      const field = this.createElementAndAppend(metadata, 'field', '' + fieldValue);
      field.setAttribute('id', fieldId);
    });
  }
}

export class MosMessageHistory {

  private MAX_MESSAGE_COUNT = 500;
  private _receivedMessages: LogMessageEvent[] = [];
  private _sentMessages: LogMessageEvent[] = [];

  get receivedMessages() {
    return this._receivedMessages;
  }

  get sentMessages() {
    return this._sentMessages;
  }

  /**
   * Log a message received from the MOS host.
   * @param message The message to log
   * @param responseKey The key of a LogMessageEvent that this message was sent as a response to
   * @returns The log event object
   */
  receivedFromHost(message: string, responseKey?: string) {
    return this.handleMessageCreation(this._receivedMessages, message, responseKey);
  }

  /**
   * Log a message sent to the MOS host.
   * @param message The message to log
   * @param responseKey The key of a LogMessageEvent that this message was sent as a response to
   * @returns The log event object
   */
  sentToHost(message: string, responseKey?: string) {
    return this.handleMessageCreation(this._sentMessages, message, responseKey);
  }

  handleMessageCreation(arr: LogMessageEvent[], message: string, responseKey?: string) {
    const msg: LogMessageEvent = {
      key: createUuid(),
      message,
      responseKey,
      timestamp: new Date().toISOString()
    };
    if (arr.length === this.MAX_MESSAGE_COUNT) {
      arr.pop();
    }
    arr.unshift(msg);
    return msg;
  }
}
